/* eslint-disable react/no-children-prop */
import { omit } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';

import Button from '@zocdoc/components/src/button';
import {
    Markdown,
    DEFAULT_RULES,
} from '@zocdoc/components/src/Markdown';

import { FontFamily } from '@zocdoc/components/src/typography';
import { prepare } from '../markdownParser';

import TooltipRenderer from './TooltipRenderer';
import Header4Renderer from './Header4Renderer';

/**
 * BIG NOTE
 *
 * If you write a custom regex, keep in mind that the source text
 * is processed left to right sequentially, so your regex should assume
 * the start of the string and look rightward.
 *
 * Captured text is then substringed out of the source block for the next
 * loop iteration to find a matching rule.
 */

// modified version of https://github.com/Khan/simple-markdown/issues/30
// const HEADING_REGEX = /^(#{1,6})([^\n]+)/;
const INLINE_CODE_REGEX = /^(`)([^`]*)(`)/;

// These two constants are taken directly from the simple-markdown definitions
// for the link rule so we can create a button rule
const LINK_INSIDE = '(?:\\[[^\\]]*\\]|[^\\[\\]]|\\](?=[^\\[]*\\]))*';
const LINK_HREF_AND_TITLE = "\\s*<?((?:[^\\s\\\\]|\\\\.)*?)>?(?:\\s+['\"]([\\s\\S]*?)['\"])?\\s*";

const BUTTON_REGEX = new RegExp(`^\\*\\[(${LINK_INSIDE})\\]\\(${LINK_HREF_AND_TITLE}\\)({([^}]+)})?`);

const InlineCodeBlock = styled.div`
    font: 15px/24px ${FontFamily.KievitSlabBook};
`;

const MDButton = styled(Button)``;

const RULES = omit(DEFAULT_RULES, [
    'autolink',
    'blockQuote',
    'codeBlock',
    'def',
    'hr',
    'lheading',
    'nptable',
    'table',
]);

const rules = {
    ...RULES,

    button: {
        order: RULES.link.order - 0.5,
        match: source => BUTTON_REGEX.exec(source),
        parse(capture, parse, state) {
            return {
                content: parse(capture[1], state),
                target: capture[2],
                title: capture[3],
                style: capture[5],
            };
        },
        react(node, output, state) {
            let style = Button.STYLE.LINK;
            switch (node.style) {
                case 'primary':
                    style = Button.STYLE.PRIMARY;
                    break;
                case 'inverted':
                    style = Button.STYLE.INVERTED;
                    break;
            }
            return (
                <MDButton
                    key={state.key}
                    href={node.target}
                    style={style}
                    title={node.title}
                >
                    {output(node.content, state)}
                </MDButton>
            );
        },
    },

    heading: {
        ...RULES.heading,

        match: source => RULES.heading.match(source, { inline: false }),

        react: (node, output, state) => {
            return (
                <Header4Renderer
                    children={output(node.content, state)}
                    key={state.key}
                    level={node.level}
                />
            );
        },
    },

    image: {
        ...RULES.image,

        react: (node, output, state) => {
            return (
                <TooltipRenderer
                    key={state.key}
                    alt={node.alt}
                    src={node.target}
                    title={node.title}
                />
            );
        },
    },

    inlineCode: {
        ...RULES.inlineCode,

        match: source => INLINE_CODE_REGEX.exec(source),

        // prevent inline code blocks from being wrapped in a paragraph context
        order: RULES.paragraph.order - 0.5,

        react: (node, __, state) => {
            return (
                <InlineCodeBlock key={state.key}>
                    {node.content}
                </InlineCodeBlock>
            );
        },
    },
};

const ModifiedMarkdown = ({
    source, data, definitions, ...props
}) => {
    const preparedSource = prepare(source, data, definitions).trim();
    return preparedSource ? (
        <Markdown
            {...props}
            rules={rules}
            source={preparedSource}
        />
    ) : null;
};

ModifiedMarkdown.propTypes = {
    source: PropTypes.string.isRequired,
    data: PropTypes.object,
    definitions: PropTypes.object,
};

export {
    MDButton,
    ModifiedMarkdown as Markdown,
};
